import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  Stack,
  Chip,
  FormControl,
  Select,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { fetchAdsByStatus, fetchAdEventAnalytics, updateAdStatus } from '../utils/api';
import { formatLocalDateTime } from '../utils/dateTime';

const TAB_OVERVIEW = 0;
const TAB_ADS_BY_STATUS = 1;
const TAB_PAYMENT_APPROVAL = 2;
const TAB_AD_ANALYTICS = 3;

/** Matches backend: filter list by status */
const FILTER_STATUS_OPTIONS = [
  { value: 0, label: 'Pending for approval' },
  { value: 1, label: 'Approved and Active' },
  { value: 2, label: 'Expired' },
  { value: 3, label: 'Rejected' },
];

/** Row editor — same values sent in PUT body */
const AD_STATUS_EDIT_OPTIONS = FILTER_STATUS_OPTIONS;

const tableHeadSx = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'text.secondary',
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap',
};

const Advertisement = () => {
  const [tab, setTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState(0);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [analyticsParentAdId, setAnalyticsParentAdId] = useState('');
  const [analyticsRows, setAnalyticsRows] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  const fetchByStatus = async (status) => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetchAdsByStatus(status);
      const data = Array.isArray(res?.data) ? res.data : [];
      setAds(data);
    } catch (err) {
      console.error('Error fetching ads by status:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to load ads.',
      });
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== TAB_ADS_BY_STATUS) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setMessage({ type: '', text: '' });
      try {
        const res = await fetchAdsByStatus(statusFilter);
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setAds(data);
      } catch (err) {
        console.error('Error fetching ads by status:', err);
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err.response?.data?.message || 'Failed to load ads.',
          });
          setAds([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tab, statusFilter]);

  const setAdStatusDraft = (adId, status) => {
    const num = Number(status);
    if (![0, 1, 2, 3].includes(num)) return;
    setAds((prev) =>
      prev.map((ad) => (ad.id === adId ? { ...ad, status: num } : ad))
    );
  };

  const handleSaveAdStatus = async (row) => {
    const adId = row?.id;
    if (adId == null) return;
    const status = Number(row.status);
    if (![0, 1, 2, 3].includes(status)) {
      setMessage({ type: 'error', text: 'Select a valid status.' });
      return;
    }
    setSavingStatusId(adId);
    setMessage({ type: '', text: '' });
    try {
      await updateAdStatus(adId, status);
      setMessage({ type: 'success', text: 'Ad status updated.' });
      await fetchByStatus(statusFilter);
    } catch (err) {
      console.error('Error updating ad status:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update ad status.',
      });
    } finally {
      setSavingStatusId(null);
    }
  };

  const handleLoadAnalytics = async () => {
    const parentAdId = String(analyticsParentAdId || '').trim();
    if (!parentAdId) {
      setAnalyticsError('Enter a parent ad ID.');
      setAnalyticsRows([]);
      return;
    }
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const res = await fetchAdEventAnalytics(parentAdId);
      setAnalyticsRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching ad analytics:', err);
      setAnalyticsError(err.response?.data?.message || 'Failed to load ad analytics.');
      setAnalyticsRows([]);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Advertisement
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, maxWidth: '100%' }}
      >
        <Tab label="Overview" />
        <Tab label="Ads by status" />
        <Tab label="Payment Approvals" />
        <Tab label="Ad Analytics" />
      </Tabs>

      {tab === TAB_OVERVIEW && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Overview
          </Typography>
          <Typography color="text.secondary">
            Use the &quot;Ads by status&quot; tab to filter ads and set status (pending, approved, expired, or rejected) per row.
          </Typography>
        </Box>
      )}

      {tab === TAB_ADS_BY_STATUS && (
        <Box>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Ads by status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              Filter the list, choose the new status per row, then use Save in the last column.
            </Typography>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  size="small"
                  label="Show ads with status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(Number(e.target.value))}
                  sx={{ minWidth: { xs: '100%', sm: 260 } }}
                >
                  {FILTER_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                {!loading && ads.length > 0 && (
                  <Chip
                    label={`${ads.length} in list`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'flex-end' } }}>
                <Tooltip title="Reload from server">
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                    onClick={() => fetchByStatus(statusFilter)}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
          </Paper>

          {message.text && (
            <Alert
              severity={message.type}
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          )}

          {loading ? (
            <Paper variant="outlined" sx={{ borderRadius: 2, py: 8 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 'min(70vh, 720px)' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tableHeadSx, width: 72 }}>ID</TableCell>
                      <TableCell sx={{ ...tableHeadSx, width: '22%' }}>Title</TableCell>
                      <TableCell sx={{ ...tableHeadSx, width: '18%' }}>Business</TableCell>
                      <TableCell sx={{ ...tableHeadSx, width: 120 }}>Expires</TableCell>
                      <TableCell sx={{ ...tableHeadSx }}>New status</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...tableHeadSx,
                          width: 140,
                          pr: 2,
                          position: 'sticky',
                          right: 0,
                          zIndex: 3,
                          bgcolor: 'action.hover',
                          boxShadow: (theme) => `-8px 0 12px -8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.08)'}`,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6, border: 'none' }}>
                          <Typography color="text.secondary" variant="body2">
                            No ads match this status. Try another filter or refresh.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ads.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            '& td': { verticalAlign: 'middle', py: 1.25 },
                            '&:hover td:last-of-type': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {row.id}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={row.title || ''}
                          >
                            {row.title || '—'}
                          </TableCell>
                          <TableCell
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={row.businessTitle || ''}
                          >
                            {row.businessTitle || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {formatLocalDateTime(row.expiryDate)}
                          </TableCell>
                          <TableCell sx={{ pr: 1 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={
                                  [0, 1, 2, 3].includes(Number(row.status)) ? Number(row.status) : 0
                                }
                                onChange={(e) => setAdStatusDraft(row.id, e.target.value)}
                                displayEmpty
                                inputProps={{ 'aria-label': 'New ad status' }}
                                sx={{ borderRadius: 1 }}
                              >
                                {AD_STATUS_EDIT_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              position: 'sticky',
                              right: 0,
                              bgcolor: 'background.paper',
                              zIndex: 1,
                              boxShadow: (theme) =>
                                `-8px 0 12px -8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.06)'}`,
                              pr: 2,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              disableElevation
                              startIcon={
                                savingStatusId === row.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                                )
                              }
                              onClick={() => handleSaveAdStatus(row)}
                              disabled={savingStatusId === row.id}
                              sx={{ minWidth: 108, borderRadius: 1.5 }}
                            >
                              {savingStatusId === row.id ? 'Saving…' : 'Save'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {tab === TAB_PAYMENT_APPROVAL && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Payment Approvals
          </Typography>
          <Typography color="text.secondary">
            Payment receipts from ad creators with approve/reject actions will be added here when the API is available.
          </Typography>
        </Box>
      )}

      {tab === TAB_AD_ANALYTICS && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Ad Analytics
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter the parent ad ID to view sub-ad event analytics.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                label="Parent Ad ID"
                value={analyticsParentAdId}
                onChange={(e) => setAnalyticsParentAdId(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 240 } }}
              />
              <Button
                variant="contained"
                onClick={handleLoadAnalytics}
                disabled={analyticsLoading}
                startIcon={analyticsLoading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {analyticsLoading ? 'Loading...' : 'Load Analytics'}
              </Button>
            </Stack>
          </Paper>

          {analyticsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAnalyticsError('')}>
              {analyticsError}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadSx}>Sub Ad ID</TableCell>
                    <TableCell sx={tableHeadSx}>Sub Ad Title</TableCell>
                    <TableCell sx={tableHeadSx}>Event Type</TableCell>
                    <TableCell sx={tableHeadSx}>Unique Users</TableCell>
                    <TableCell sx={tableHeadSx}>Total Event</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : analyticsRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" variant="body2">
                          No analytics found for this ad.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    analyticsRows.map((row, idx) => (
                      <TableRow key={`${row.subAdId}-${row.eventType}-${idx}`}>
                        <TableCell>{row.subAdId ?? '-'}</TableCell>
                        <TableCell>{row.subAdTitle || '-'}</TableCell>
                        <TableCell>{row.eventType || '-'}</TableCell>
                        <TableCell>{row.uniqueUsers ?? 0}</TableCell>
                        <TableCell>{row.totalEvent ?? 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Advertisement;
